import { useEffect, useRef } from 'react';
import { loadTurnstileApi } from '../Utils/Captcha/loadTurnstileApi';
import { generateMockToken } from '../Utils/Captcha/generateMockToken';
import { config } from '../Configs';

const CAPTCHA_DISABLED = config.turnstile.disableValidation;

const hasWidgetId = widgetId => widgetId !== null && widgetId !== undefined;
const hasMethod = (obj, methodName) =>
  obj && typeof obj[methodName] === 'function';

export const useTurnstileWidget = ({
  action = null,
  captchaRef,
  disabledCaptcha,
  execution,
  locale,
  onBeforeInteractive = () => {},
  onExpired,
  onError,
  onLoaded,
  onResolved,
  siteKey,
  size,
  tabindex,
  theme
}) => {
  const containerRef = useRef(null);
  const turnstileIdRef = useRef(null);
  const isExecutingRef = useRef(false);
  const isRenderedRef = useRef(false);

  // Always holds the latest callbacks without exposing them as effect deps.
  const callbacksRef = useRef(null);
  callbacksRef.current = {
    onBeforeInteractive,
    onExpired,
    onError,
    onLoaded,
    onResolved
  };

  useEffect(() => {
    let isCancelled = false;

    if (isRenderedRef.current) {
      return undefined;
    }

    if (CAPTCHA_DISABLED || disabledCaptcha) {
      isRenderedRef.current = true;
      callbacksRef.current.onResolved(generateMockToken());
      // onLoaded must be called to set loading:false and unblock waitForCaptchaReady in useCaptcha.
      callbacksRef.current.onLoaded();
      return undefined;
    }

    const getApi = () => window.turnstile;

    const resetWidget = () => {
      isExecutingRef.current = false;

      const api = getApi();
      if (!hasWidgetId(turnstileIdRef.current) || !hasMethod(api, 'reset')) {
        return;
      }

      api.reset(turnstileIdRef.current);
    };

    const removeWidget = () => {
      const api = getApi();
      if (!hasWidgetId(turnstileIdRef.current)) {
        return;
      }

      if (hasMethod(api, 'remove')) {
        api.remove(turnstileIdRef.current);
        return;
      }

      if (hasMethod(api, 'reset')) {
        api.reset(turnstileIdRef.current);
      }
    };

    const exposeRefApi = api => {
      if (!captchaRef) {
        return;
      }

      captchaRef.current = {
        action,
        reset: resetWidget,
        getResponse: () => {
          if (
            !hasWidgetId(turnstileIdRef.current) ||
            !hasMethod(api, 'getResponse')
          ) {
            return null;
          }

          return api.getResponse(turnstileIdRef.current);
        },
        execute: () => {
          if (isExecutingRef.current) {
            return false;
          }

          if (
            !hasWidgetId(turnstileIdRef.current) ||
            !hasMethod(api, 'execute')
          ) {
            return false;
          }

          resetWidget();
          isExecutingRef.current = true;
          api.execute(turnstileIdRef.current);

          return true;
        }
      };
    };

    const renderTurnstile = () => {
      if (!containerRef.current || isRenderedRef.current || isCancelled) {
        return;
      }

      const api = getApi();
      if (!hasMethod(api, 'render')) {
        return;
      }

      while (containerRef.current.firstChild) {
        containerRef.current.removeChild(containerRef.current.firstChild);
      }

      const wrapper = document.createElement('div');
      containerRef.current.appendChild(wrapper);

      const turnstileOptions = {
        sitekey: siteKey,
        // Wrapper functions so Turnstile always invokes the latest callback
        // even though the widget is only rendered once.
        callback: token => {
          isExecutingRef.current = false;
          callbacksRef.current.onResolved(token);
        },
        'error-callback': () => {
          isExecutingRef.current = false;
          callbacksRef.current.onError();
        },
        'expired-callback': () => {
          isExecutingRef.current = false;
          callbacksRef.current.onExpired();
        },
        'timeout-callback': () => {
          isExecutingRef.current = false;
          callbacksRef.current.onError();
        },
        'before-interactive-callback': () =>
          callbacksRef.current.onBeforeInteractive(),
        tabindex,
        language: locale || 'auto',
        theme,
        size
      };

      if (action) {
        turnstileOptions.action = action;
      }

      if (execution) {
        turnstileOptions.execution = execution;
      }

      turnstileIdRef.current = api.render(wrapper, turnstileOptions);
      isRenderedRef.current = true;
      exposeRefApi(api);
      callbacksRef.current.onLoaded();
    };

    const initializeTurnstile = async () => {
      const api = await loadTurnstileApi();

      if (isCancelled || !hasMethod(api, 'render')) {
        return;
      }

      renderTurnstile();
    };

    initializeTurnstile();

    return () => {
      isCancelled = true;
      isExecutingRef.current = false;
      removeWidget();
      isRenderedRef.current = false;
      turnstileIdRef.current = null;

      if (captchaRef) {
        captchaRef.current = null;
      }
    };
  }, [
    action,
    captchaRef,
    disabledCaptcha,
    execution,
    locale,
    siteKey,
    size,
    tabindex,
    theme
  ]);

  return containerRef;
};
